import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  RefreshOutlined,
  SearchOutlined,
  SupervisorAccountOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { hubspotDealUrl } from '../hubspot';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  assignCRN,
  clearSearch,
  fetchAccounts,
  fetchCRNAvailability,
  fetchMoreAccounts,
  searchAccounts,
  selectAccounts,
  selectAccountsStatus,
  selectAssignError,
  selectAssignResult,
  selectAssignStatus,
  selectCRN,
  selectCRNStatus,
  selectNextToken,
  selectSearchQuery,
  selectSearchResults,
  selectSearchStatus,
  selectServicesById,
  selectServicesStatusById,
  selectSyncStatusById,
  syncAccount,
} from '../features/accounts/accounts';
import { fetchPlans, selectPlans, Plan } from '../features/plans/plans';
import { getOurDealStage } from '../utils';
import { cleanLocationId } from './LocationPage';
import { Account, Service } from './models';

export const CHARGEBEE_SITE = 'pineapple';
const CB = `https://${CHARGEBEE_SITE}.chargebee.com/d`;
export const chargebeeCustomerUrl = (id: string) => `${CB}/customers/${id}`;
export const chargebeeSubscriptionUrl = (id: string) => `${CB}/subscriptions/${id}`;
export const chargebeePlanUrl = (id: string) => `${CB}/plans/${id}`;
// Kept exported here because other pages import it from Accounts.
export { hubspotDealUrl };

const CRN_LOW_THRESHOLD = 500;

type Highlight = 'error' | 'success' | 'warning';
const CRNStat = (
  { label, value, highlight = undefined }:
  { label: string; value: number; highlight?: Highlight },
) => (
  <Box sx={{ textAlign: 'center', minWidth: 80 }}>
    <Typography
      variant="h6"
      lineHeight={1}
      color={highlight ? `${highlight}.main` : 'text.primary'}
    >
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const CRNBadge = () => {
  const crn = useAppSelector(selectCRN);
  const status = useAppSelector(selectCRNStatus);

  if (status === 'loading') {
    return <Skeleton variant="rounded" width={380} height={40} />;
  }

  if (!crn || status === 'failed') {
    return null;
  }

  const isLow = crn.available < CRN_LOW_THRESHOLD;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 0.5,
        border: 1,
        borderColor: isLow ? 'error.main' : 'divider',
        borderRadius: 1,
      }}
    >
      {isLow && <WarningAmberOutlined fontSize="small" color="error" />}
      <CRNStat label="total" value={crn.total} />
      <Divider orientation="vertical" flexItem />
      <CRNStat label="allocated" value={crn.allocated} />
      <Divider orientation="vertical" flexItem />
      <CRNStat label="quarantined (60d)" value={crn.quarantined} highlight="warning" />
      <Divider orientation="vertical" flexItem />
      <CRNStat label="available" value={crn.available} highlight={isLow ? 'error' : 'success'} />
    </Box>
  );
};

// CRNAssignTool lets an operator populate or rotate a Chargebee customer's BPAY
// CRN. It accepts a Chargebee customer ID or, if an "@" is present, an email
// (the backend resolves the customer by email first).
const CRNAssignTool = () => {
  const dispatch = useAppDispatch();
  const result = useAppSelector(selectAssignResult);
  const status = useAppSelector(selectAssignStatus);
  const error = useAppSelector(selectAssignError);

  const [query, setQuery] = useState('');
  const loading = status === 'loading';
  const trimmed = query.trim();

  const submit = (rotate: boolean) => {
    if (!trimmed || loading) return;
    const isEmail = trimmed.includes('@');
    dispatch(assignCRN({
      customerId: isEmail ? undefined : trimmed,
      email: isEmail ? trimmed : undefined,
      rotate,
    }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, mt: 2, border: 1, borderColor: 'divider',
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5,
      }}
      >
        <SupervisorAccountOutlined fontSize="small" />
        <Typography variant="subtitle1">Assign / Rotate BPAY CRN</Typography>
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      }}
      >
        <TextField
          size="small"
          placeholder="Chargebee customer ID or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(false); }}
          sx={{ width: 320 }}
        />
        <Tooltip title="Set the CRN only if the customer doesn't already have one">
          <span>
            <Button
              variant="contained"
              onClick={() => submit(false)}
              disabled={!trimmed || loading}
              startIcon={loading ? <CircularProgress size={14} /> : undefined}
            >
              Populate CRN
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Replace the existing CRN with a fresh one (old CRN is quarantined for 60 days)">
          <span>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => submit(true)}
              disabled={!trimmed || loading}
              startIcon={<RefreshOutlined fontSize="small" />}
            >
              Rotate CRN
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{error}</Typography>
      )}

      {result && !loading && !error && (
        <Box sx={{
          mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        }}
        >
          {result.rotated && <Chip size="small" color="warning" label="Rotated" />}
          {result.created && <Chip size="small" color="success" label="Assigned" />}
          {!result.rotated && !result.created && (
            <Chip size="small" variant="outlined" label="Already set" />
          )}
          <Typography variant="body2">
            CRN
            {' '}
            <Box component="span" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{result.crn}</Box>
            {result.rotated && result.previous_crn && (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {' '}
                (was
                {' '}
                {result.previous_crn}
                )
              </Box>
            )}
            {' · '}
            {result.email || result.customer_id}
          </Typography>
          <Box
            component="a"
            href={chargebeeCustomerUrl(result.customer_id)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'primary.main',
              textDecoration: 'none',
            }}
          >
            View in Chargebee
            <OpenInNew sx={{ fontSize: 14 }} />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

const CbLink = ({ href, label }: { href: string; label: string }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      color: 'primary.main',
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    {label}
    <OpenInNew sx={{ fontSize: 12 }} />
  </Box>
);

const subStatusChip = (svc: Service) => {
  if (svc.deleted_at !== 0) {
    return <Chip label="Cancelled" size="small" color="default" />;
  }
  const active = svc.cb_active === 'true' || svc.cb_active === 'active';
  return active
    ? <Chip label="Active" size="small" color="success" variant="outlined" />
    : <Chip label="Inactive" size="small" color="warning" variant="outlined" />;
};

const ServicesTable = (
  { services, plansById }:
  { services: Service[]; plansById: Record<string, Plan> },
) => {
  if (services.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No services
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Status</TableCell>
          <TableCell>Subscription ID</TableCell>
          <TableCell>Plan</TableCell>
          <TableCell>Location</TableCell>
          <TableCell>Start Date</TableCell>
          <TableCell>HubSpot</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {services.map((svc) => {
          const plan = svc.plan_id ? plansById[svc.plan_id] : undefined;
          return (
            <TableRow key={svc.id}>
              <TableCell>{subStatusChip(svc)}</TableCell>
              <TableCell>
                {svc.cb_subscription_id
                  ? (
                    <CbLink
                      href={chargebeeSubscriptionUrl(svc.cb_subscription_id)}
                      label={svc.cb_subscription_id}
                    />
                  )
                  : '—'}
              </TableCell>
              <TableCell>
                {svc.plan_id
                  ? (
                    <CbLink
                      href={chargebeePlanUrl(svc.plan_id)}
                      label={plan?.name ?? svc.plan_id}
                    />
                  )
                  : '—'}
              </TableCell>
              <TableCell>
                {svc.location_id
                  ? (
                    <Box
                      component={Link}
                      to={`/dashboard/accounts/location/${svc.location_id}`}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {cleanLocationId(svc.location_id)}
                    </Box>
                  )
                  : '—'}
              </TableCell>
              <TableCell>
                {svc.cb_sub_start_date
                  ? new Date(svc.cb_sub_start_date).toLocaleDateString()
                  : '—'}
              </TableCell>
              <TableCell>
                {svc.hubspot_deal_id
                  ? (
                    <CbLink
                      href={hubspotDealUrl(svc.hubspot_deal_id)}
                      label={getOurDealStage(svc.hubspot_deal_stage) || svc.hubspot_deal_id}
                    />
                  )
                  : '—'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const AccountRow = (
  { account, plansById }:
  { account: Account; plansById: Record<string, Plan> },
) => {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const servicesById = useAppSelector(selectServicesById);
  const servicesStatusById = useAppSelector(selectServicesStatusById);
  const syncStatusById = useAppSelector(selectSyncStatusById);

  const services = servicesById[account.id];
  const svcStatus = servicesStatusById[account.id] ?? 'idle';
  const isSyncing = syncStatusById[account.id] === 'syncing';
  const isFreed = account.deleted_at !== 0;

  const handleToggle = () => {
    if (!open) {
      dispatch(syncAccount(account.id));
    }
    setOpen((prev) => !prev);
  };

  return (
    <>
      <TableRow
        hover
        onClick={handleToggle}
        sx={{ cursor: 'pointer', opacity: isFreed ? 0.65 : 1 }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggle(); }}>
            {open
              ? <KeyboardArrowUp fontSize="small" />
              : <KeyboardArrowDown fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{account.sk}</TableCell>
        <TableCell>{account.email}</TableCell>
        <TableCell>{account.first_name}</TableCell>
        <TableCell>{account.last_name}</TableCell>
        <TableCell>
          {isFreed
            ? <Chip label="Freed" size="small" color="default" />
            : <Chip label="Active" size="small" color="success" variant="outlined" />}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          {account.cb_customer_id
            ? (
              <CbLink
                href={chargebeeCustomerUrl(account.cb_customer_id)}
                label={account.cb_customer_id}
              />
            )
            : null}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, pl: 7, pr: 2 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, mb: 1,
              }}
              >
                {(isSyncing || svcStatus === 'loading') && (
                  <>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {isSyncing ? 'Syncing from Chargebee…' : 'Loading…'}
                    </Typography>
                  </>
                )}
                {!isSyncing && account.last_synced_at > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Last synced:
                    {' '}
                    {new Date(account.last_synced_at * 1000).toLocaleString()}
                  </Typography>
                )}
                <Tooltip title="Force sync & backfill from Chargebee / HubSpot">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isSyncing}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(syncAccount(account.id));
                      }}
                    >
                      <RefreshOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {svcStatus === 'failed' && (
                <Typography variant="body2" color="error">Failed to load services</Typography>
              )}
              {services && !isSyncing && <ServicesTable services={services} plansById={plansById} />}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const matchesSearch = (account: Account, q: string) => {
  const lower = q.toLowerCase();
  return (
    account.email.toLowerCase().includes(lower)
    || account.sk.toLowerCase().includes(lower)
    || account.first_name.toLowerCase().includes(lower)
    || account.last_name.toLowerCase().includes(lower)
    || `${account.first_name} ${account.last_name}`.toLowerCase().includes(lower)
  );
};

export const AccountsServicesPage = () => {
  const dispatch = useAppDispatch();

  const accounts = useAppSelector(selectAccounts);
  const accountsStatus = useAppSelector(selectAccountsStatus);
  const nextToken = useAppSelector(selectNextToken);
  const crn = useAppSelector(selectCRN);
  const plans = useAppSelector(selectPlans);

  const plansById = useMemo(
    () => Object.fromEntries(plans.map((p) => [p.id, p])),
    [plans],
  );

  const searchResults = useAppSelector(selectSearchResults);
  const searchQuery = useAppSelector(selectSearchQuery);
  const searchStatus = useAppSelector(selectSearchStatus);

  const [searchTerm, setSearchTerm] = useState('');

  // When a server search is active we show its exact results; otherwise we
  // client-side filter the already-loaded page of accounts.
  const serverActive = searchResults !== null;

  const filtered = useMemo(
    () => (searchTerm.trim() ? accounts.filter((a) => matchesSearch(a, searchTerm)) : accounts),
    [accounts, searchTerm],
  );

  const rows = serverActive ? (searchResults ?? []) : filtered;
  const showSkeleton = (accountsStatus === 'loading' && accounts.length === 0)
    || searchStatus === 'loading';

  // Only load on first mount — don't reload when navigating back.
  useEffect(() => {
    if (accounts.length === 0) {
      dispatch(fetchAccounts());
    }
    if (!crn) {
      dispatch(fetchCRNAvailability());
    }
    if (plans.length === 0) {
      dispatch(fetchPlans());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runServerSearch = () => {
    const q = searchTerm.trim();
    if (q) {
      dispatch(searchAccounts(q));
    }
  };

  const clearServerSearch = () => {
    setSearchTerm('');
    dispatch(clearSearch());
  };

  const handleRefresh = () => {
    dispatch(clearSearch());
    dispatch(fetchAccounts());
    dispatch(fetchCRNAvailability());
  };

  return (
    <Box sx={{ mr: 15, ml: 15, mt: 10 }}>
      <CRNBadge />

      <CRNAssignTool />

      <Paper elevation={0} sx={{ p: 2, mt: 2 }}>
        <Toolbar disableGutters sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SupervisorAccountOutlined />
            <Typography variant="h6">Accounts</Typography>
            {serverActive ? (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${rows.length} result${rows.length === 1 ? '' : 's'} for “${searchQuery}”`}
                onDelete={clearServerSearch}
              />
            ) : accounts.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {searchTerm.trim()
                  ? `${filtered.length} of ${accounts.length}`
                  : `${accounts.length}${nextToken ? '+' : ''}`}
                {' '}
                loaded
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Filters loaded rows as you type. Press Enter to search ALL accounts by CRN, email, or customer id.">
              <TextField
                size="small"
                placeholder="Search CRN, email, customer id…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runServerSearch(); }}
                sx={{ width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              onClick={runServerSearch}
              disabled={!searchTerm.trim() || searchStatus === 'loading'}
              startIcon={
                searchStatus === 'loading'
                  ? <CircularProgress size={14} />
                  : <SearchOutlined fontSize="small" />
              }
            >
              Search
            </Button>
            {serverActive && (
              <Button size="small" variant="outlined" onClick={clearServerSearch}>
                Clear
              </Button>
            )}
            {!serverActive && nextToken && !searchTerm.trim() && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => dispatch(fetchMoreAccounts())}
                disabled={accountsStatus === 'loading'}
                startIcon={
                  accountsStatus === 'loading'
                    ? <CircularProgress size={14} />
                    : undefined
                }
              >
                Load more
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} size="small">
                <RefreshOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>CRN</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Chargebee</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showSkeleton && Array.from({ length: 5 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!showSkeleton && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      {serverActive
                        ? `No account found for “${searchQuery}”.`
                        : 'No accounts to show.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!showSkeleton && rows.map((account) => (
                <AccountRow key={`${account.id}|${account.sk}`} account={account} plansById={plansById} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </Paper>
    </Box>
  );
};
